import { useEffect, useState } from "react";
import {
  CustomEditor,
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  getHelpTopicById,
  handleSubmitHelpTopic,
  HELP_TOPIC_FIELD_INITIAL_VALUES,
  helpTopicsFieldValidationSchema,
  IHelpTopicForm,
} from "./HelpTopicsFormController";
import { getHelpCategoryDdl, IHelpTopic } from "./HelpTopicsViewController";
import {
  ICFormikDropDown,
  ICFormikSwitch,
  ICFormikTextInput,
} from "../../core-component/formik";
import { Form, Formik } from "formik";
import { hasAnyModification } from "../../utils/AppFunctions";
import {
  BIT_VALUE,
  ENTITY_TYPE,
  UPLOAD_DOCUMENT_TYPE,
} from "../../utils/AppEnumerations";
import { IMediaItem, IOption } from "../../data/AppInterface";
import { ICFileUpload } from "../../core-component/form";

import {
  uploadDocumentService,
  fetchUploadImageService,
  uploadVideoService,
} from "../global/image-service/ImageServices";

interface IHelpTopicsFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  helpTopicToEdit?: IHelpTopic;
}

const HelpTopicForm: React.FC<IHelpTopicsFormProps> = ({
  handleClose,
  readOnly,
  helpTopicToEdit,
}) => {
  const { t } = UseTranslationHook();
  const [initialValues, setInitialValues] = useState<IHelpTopicForm>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [helpCategory, setHelpCategory] = useState<IOption<string>[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadFiles] = useState<IMediaItem[]>([]);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    const initialize = async () => {
      let requiredId = null;
      if (helpTopicToEdit) {
        await fetchHelpTopicById(helpTopicToEdit.id);
        requiredId = helpTopicToEdit.help_category_id || null;
      } else {
        setInitialValues(HELP_TOPIC_FIELD_INITIAL_VALUES);
      }
      await getHelpCategoryDdl(setHelpCategory, requiredId, true);
    };
    initialize();
  }, []);

  const fetchHelpTopicById = async (id: string) => {
    setLoading(true);
    await getHelpTopicById(
      id,
      handleClose,
      setInitialValues,
      setUploadFiles,
      setTags,
    );
    setLoading(false);
  };
  const checkFormModification = (values: IHelpTopicForm) => {
    if (initialValues && hasAnyModification(values, initialValues)) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = async (values: IHelpTopicForm) => {
    setSubmitLoading(true);
    let imageUrl: string | null = null;
    let pdfUrl: string | null = null;
    let videoUrl: string | null = null;
    if (uploadedFiles.length > 0) {
      for (const doc of uploadedFiles) {
        if (doc.media_type === UPLOAD_DOCUMENT_TYPE.Image) {
          imageUrl = doc.media_url;
        } else if (doc.media_type === UPLOAD_DOCUMENT_TYPE.Video) {
          videoUrl = doc.media_url;
        } else if (doc.media_type === UPLOAD_DOCUMENT_TYPE.Pdf) {
          pdfUrl = doc.media_url;
        }
      }
    }

    for (const file of selectedFiles) {
      const formData = new FormData();

      formData.append("type", ENTITY_TYPE.HelpTopic);
      if (values.id) {
        formData.append("typeId", values.id.toString());
      }
      if (file.type.startsWith("image/") && !imageUrl) {
        formData.append("ImageFile", file);
        imageUrl = await fetchUploadImageService(formData);
      } else if (file.type === "application/pdf" && !pdfUrl) {
        formData.append("DocumentFile", file);
        pdfUrl = await uploadDocumentService(formData);
      } else if (file.type === "video/mp4" && !videoUrl) {
        formData.append("videoFile", file);
        videoUrl = await uploadVideoService(formData);
      } else {
        continue;
      }
    }

    await handleSubmitHelpTopic(
      {
        ...values,
        tags: values.tags?.length ? values.tags : null,
        image_url: imageUrl,
        pdf_url: pdfUrl,
        video_url: videoUrl,
        reference_link: values.reference_link || null,
      },
      setSubmitLoading,
      handleClose,
      helpTopicToEdit?.id,
    );
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {initialValues && (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={helpTopicsFieldValidationSchema(t)}
              onSubmit={(value) => handleSubmit(value)}
            >
              {(formikProps) => {
                const { handleSubmit, values } = formikProps;
                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/3"
                    onClose={() => checkFormModification(values)}
                    title={t("help_topic")}
                    footer={{
                      created_date: initialValues.created_date,
                      modified_date: initialValues.modified_date,
                      created_by: initialValues.created_by,
                      modified_by: initialValues.modified_by,
                    }}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 overflow-y-auto py-4 space-y-4">
                        <ICFormikTextInput
                          {...formikProps}
                          fieldName="topic_title"
                          placeholder={t("topic_title")}
                          required
                          disabled={readOnly}
                        />
                        <ICFormikDropDown
                          {...formikProps}
                          name={"help_category_id"}
                          placeholder={t("help_category")}
                          options={helpCategory}
                          required
                          searchable
                          disabled={readOnly}
                        />
                        <ICFormikTextInput
                          {...formikProps}
                          fieldName="display_sequence"
                          placeholder={t("display_sequence")}
                          type="number"
                          required
                          disabled={readOnly}
                        />

                        <div>
                          <label>{t("tags")}</label>
                          <div className="flex flex-wrap  p-2 border rounded hover:border-black focus-within:border-accent">
                            {formikProps.values.tags?.map((tag, index) => (
                              <div
                                key={index}
                                className="flex items-center m-1 px-2 py-1  bg-accent-muted rounded-full text-sm"
                              >
                                {tag}
                                {!readOnly && (
                                  <span
                                    onClick={() => {
                                      const updatedTags = [
                                        ...(formikProps.values.tags || []),
                                      ];
                                      updatedTags.splice(index, 1);
                                      formikProps.setFieldValue(
                                        "tags",
                                        updatedTags,
                                      );
                                    }}
                                    className="ml-2 text-danger font-bold cursor-pointer"
                                  >
                                    ×
                                  </span>
                                )}
                              </div>
                            ))}
                            {!readOnly && (
                              <input
                                type="text"
                                onKeyDown={(e) => {
                                  const input = e.target as HTMLInputElement;
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const value = input.value.trim();
                                    if (
                                      value &&
                                      !formikProps.values.tags?.includes(value)
                                    ) {
                                      formikProps.setFieldValue("tags", [
                                        ...(formikProps.values.tags || []),
                                        value,
                                      ]);
                                      input.value = "";
                                    }
                                  }
                                }}
                                placeholder={t("tags")}
                                className="flex-1 p-1 outline-none border-none text-sm"
                              />
                            )}
                          </div>
                        </div>

                        <ICFormikTextInput
                          {...formikProps}
                          fieldName="reference_link"
                          placeholder={t("reference_link")}
                          disabled={readOnly}
                        />
                        <div>
                          <CustomEditor
                            formikPorps={formikProps}
                            fieldName="topic_description"
                            text={initialValues.topic_description}
                            displayName={`${t("topic_description")}*`}
                            enableHtmlMode
                          />
                          {formikProps.touched.topic_description &&
                            formikProps.errors.topic_description && (
                              <div className="text-sm text-danger">
                                {formikProps.errors.topic_description}
                              </div>
                            )}
                        </div>
                        {values.id && (
                          <ICFormikSwitch
                            {...formikProps}
                            name={"is_active"}
                            containerClassName="gap-4 !w-fit"
                            placeholder={t("status")}
                            checkedValue={BIT_VALUE.One}
                            unCheckedValue={BIT_VALUE.Zero}
                            disabled={readOnly}
                          />
                        )}
                        <div className="mt-4">
                          <ICFileUpload
                            title={t("upload_files")}
                            maxFiles={3}
                            maxFileSizeMB={10}
                            acceptedFileTypes={[
                              "jpg",
                              "png",
                              "jpeg",
                              "mp4",
                              "pdf",
                              "m4a",
                            ]}
                            onFilesSelected={setSelectedFiles}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            data={uploadedFiles}
                            setData={setUploadFiles}
                            disabled={readOnly}
                            uniqueMedia
                          />
                        </div>
                      </div>

                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className="w-full mt-2"
                        >
                          {t("submit")}
                        </ICButton>
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
        </>
      )}
      {showDiscardWarning && (
        <ICConfirmationModal
          title={t("discard_warning")}
          message={t("discard_unsaved_changes")}
          onClose={(confirm) => {
            setShowDiscardWarning(false);
            if (confirm) handleClose(false);
          }}
        />
      )}
    </>
  );
};

export default HelpTopicForm;
